'use client';
import { useState, useCallback, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
    ClipboardList, Download, RefreshCw, BarChart3, Layers, CheckCircle,
    Upload, FileSpreadsheet, AlertTriangle, Sparkles, X, Server, FolderOpen
} from 'lucide-react';
import { useTheme } from '@/components/ThemeContext';
import { AgentPageLayout } from '@/components/AgentPageLayout';
import { AGENT_CONFIGS } from '@/lib/agent-constants';
import { AgentToolbar } from '@/components/AgentToolbar';

interface ParseResult {
    fileName: string; sheetName: string; headers: string[]; totalRows: number;
    preview: Record<string, any>[];
}
interface Issue {
    type: string; item: string; detail: string; severity: string;
}
interface CategoryBreakdown {
    category: string; amount: number; ratio: number; itemCount: number;
}
interface Suggestion {
    title: string; detail: string; impact: string;
}
interface AnalysisResult {
    summary: { totalAmount: number; categoryCount: number; itemCount: number; unitPricePerSqm: number };
    issues: Issue[];
    categoryBreakdown: CategoryBreakdown[];
    suggestions: Suggestion[];
}
interface NasFile {
    name: string; path: string; size: number;
}

export default function BOQAgentPage() {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const config = AGENT_CONFIGS.boq;
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [parseResult, setParseResult] = useState<ParseResult | null>(null);
    const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
    const [streamText, setStreamText] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [stage, setStage] = useState(0); // 0: ready, 1: uploading, 2: parsing, 3: analyzing, 4: complete
    const [aiDone, setAiDone] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [nasFiles, setNasFiles] = useState<NasFile[]>([]);
    const [nasConnected, setNasConnected] = useState(false);
    const [showNas, setShowNas] = useState(false);
    const [error, setError] = useState('');

    // NAS 파일 목록 조회
    const loadNasFiles = useCallback(async () => {
        try {
            const res = await fetch('/api/boq-agent');
            const data = await res.json();
            setNasConnected(data.nasConnected);
            setNasFiles(data.files || []);
            setShowNas(true);
        } catch { setError('NAS 연결 실패'); }
    }, []);

    // 파일 분석
    const analyzeFile = useCallback(async (file?: File, nasPath?: string) => {
        setError('');
        setAnalysis(null);
        setStreamText('');
        setStage(1);
        setIsProcessing(true);
        setAiDone(false);

        try {
            const formData = new FormData();
            if (file) formData.append('file', file);
            if (nasPath) formData.append('nasPath', nasPath);

            const res = await fetch('/api/boq-agent', { method: 'POST', body: formData });

            if (!res.ok) {
                const err = await res.json();
                setError(err.error || 'API 오류');
                setStage(0);
                setIsProcessing(false);
                return;
            }

            setStage(2);
            const reader = res.body?.getReader();
            if (!reader) return;

            const decoder = new TextDecoder();
            let fullText = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value, { stream: true });
                const lines = chunk.split('\n');

                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue;
                    try {
                        const data = JSON.parse(line.substring(6));
                        if (data.type === 'parse_complete') {
                            setParseResult(data as ParseResult);
                            setStage(3);
                        } else if (data.type === 'chunk') {
                            fullText += data.text;
                            setStreamText(fullText);
                        } else if (data.type === 'complete') {
                            try {
                                const jsonMatch = data.fullText.match(/\{[\s\S]*\}/);
                                if (jsonMatch) {
                                    const parsed = JSON.parse(jsonMatch[0]);
                                    setAnalysis(parsed);
                                }
                            } catch { /* 파싱 실패 시 텍스트만 표시 */ }
                            setStage(4);
                            setAiDone(true);
                        }
                    } catch { /* skip */ }
                }
            }
        } catch (e: any) {
            setError(e.message || '분석 실패');
            setStage(0);
        } finally {
            setIsProcessing(false);
        }
    }, []);

    // 드래그&드롭 핸들러
    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file && /\.(xlsx?|csv)$/i.test(file.name)) analyzeFile(file);
        else setError('Excel 파일(.xlsx, .xls, .csv)만 지원합니다');
    }, [analyzeFile]);

    const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) analyzeFile(file);
    }, [analyzeFile]);

    const severityColor = (s: string) => {
        if (s === 'high') return isDark ? 'text-red-400 bg-red-500/10' : 'text-red-600 bg-red-50';
        if (s === 'medium') return isDark ? 'text-amber-400 bg-amber-500/10' : 'text-amber-600 bg-amber-50';
        return isDark ? 'text-gray-400 bg-gray-500/10' : 'text-gray-500 bg-gray-100';
    };

    const issueIcon = (type: string) => {
 if (type === 'missing') return '';
 if (type === 'anomaly') return '';
 if (type === 'overpriced') return '';
 if (type === 'underpriced') return '';
 return '';
    };

    return (
        <AgentPageLayout config={config} icon={<ClipboardList className="w-5 h-5" />} isProcessing={isProcessing} aiDone={aiDone}>
            {/* Error */}
            <AnimatePresence>
                {error && (
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        className={`mb-4 p-3 rounded-xl flex items-center gap-2 text-sm ${isDark ? 'bg-red-500/10 text-red-300 border border-red-500/20' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                        <AlertTriangle className="w-4 h-4 shrink-0" /> {error}
                        <button onClick={() => setError('')} className="ml-auto"><X className="w-4 h-4" /></button>
                    </motion.div>
                )}
            </AnimatePresence>

            {stage === 0 ? (
                /* ═══ Upload Zone ═══ */
                <div className="grid md:grid-cols-2 gap-4">
                    {/* Drag & Drop */}
                    <div
                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        className={`rounded-2xl border-2 border-dashed p-12 text-center cursor-pointer transition-all ${
                            dragOver
                                ? isDark ? `border-cyan-400 ${config.accentBg}` : 'border-cyan-400 bg-cyan-50'
                                : isDark ? 'border-white/10 bg-gray-900/50 hover:border-cyan-500/30' : 'border-gray-200 bg-white hover:border-cyan-300'
                        }`}
                    >
                        <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFileSelect} className="hidden" />
                        <Upload className={`w-12 h-12 mx-auto mb-4 ${dragOver ? 'text-cyan-400' : isDark ? 'text-cyan-500/30' : 'text-cyan-300'}`} />
                        <p className="font-bold mb-1">Excel 내역서 업로드</p>
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            .xlsx / .xls / .csv 파일을 드래그하거나 클릭하세요
                        </p>
                        <div className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold ${isDark ? `${config.accentBg} ${config.accentText}` : 'bg-cyan-50 text-cyan-700'}`}>
                            <FileSpreadsheet className="w-4 h-4" /> 파일 선택
                        </div>
                    </div>

                    {/* NAS Browse */}
                    <div className={`rounded-2xl border p-6 ${isDark ? 'border-white/10 bg-gray-900/50' : 'border-gray-200 bg-white shadow-sm'}`}>
                        <div className="flex items-center gap-2 mb-4">
                            <Server className="w-5 h-5 text-blue-400" />
                            <span className="font-bold text-sm">NAS 내역서 탐색</span>
                            {nasConnected && <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />}
                        </div>
                        <button onClick={loadNasFiles}
                            className={`w-full py-3 rounded-xl text-xs font-bold transition cursor-pointer flex items-center justify-center gap-2 ${isDark ? 'bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 border border-blue-500/20' : 'bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200'}`}>
                            <FolderOpen className="w-4 h-4" /> NAS에서 내역서 찾기
                        </button>
                        <AnimatePresence>
                            {showNas && (
                                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="mt-3 space-y-1.5 overflow-hidden">
                                    {nasFiles.length === 0 ? (
                                        <p className={`text-xs text-center py-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                            {nasConnected ? '내역서 파일이 없습니다' : 'NAS 미연결 — Finder에서 마운트하세요'}
                                        </p>
                                    ) : nasFiles.map(f => (
                                        <button key={f.path} onClick={() => analyzeFile(undefined, f.path)}
                                            className={`w-full text-left px-3 py-2.5 rounded-xl text-[11px] transition cursor-pointer ${isDark ? 'hover:bg-white/5 border border-white/5' : 'hover:bg-gray-50 border border-gray-100'}`}>
                                            <div className="flex items-center gap-2">
                                                <FileSpreadsheet className="w-4 h-4 text-green-400 shrink-0" />
                                                <div className="min-w-0 flex-1">
                                                    <div className="font-semibold truncate">{f.name}</div>
                                                    <div className={`text-[10px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{(f.size/1024/1024).toFixed(1)} MB</div>
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            ) : (
                /* ═══ Analysis Results ═══ */
                <div className="space-y-4">
                    {/* Parse Info */}
                    {parseResult && (
                        <div className={`rounded-2xl border p-4 ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-white shadow-sm'}`}>
                            <div className="flex items-center gap-3 mb-2">
                                <FileSpreadsheet className="w-5 h-5 text-green-400" />
                                <span className="font-bold text-sm">{parseResult.fileName}</span>
                                <span className={`text-[10px] px-2 py-0.5 rounded ${isDark ? 'bg-green-500/10 text-green-400' : 'bg-green-50 text-green-600'}`}>
                                    {parseResult.totalRows}행 · {parseResult.headers.length}열 · 시트: {parseResult.sheetName}
                                </span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                                {parseResult.headers.map(h => (
                                    <span key={h} className={`text-[10px] px-2 py-0.5 rounded ${isDark ? 'bg-white/5 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>{h}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Streaming / Analysis */}
                    {stage >= 3 && !analysis && (
                        <div className={`rounded-2xl border p-5 ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-white shadow-sm'}`}>
                            <div className="flex items-center gap-2 mb-3">
                                <Sparkles className={`w-4 h-4 ${config.accentText} animate-pulse`} />
                                <span className="text-sm font-bold">AI 수석 견적사 분석 중...</span>
                            </div>
                            <pre className={`text-xs whitespace-pre-wrap font-mono leading-relaxed max-h-[400px] overflow-y-auto ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {streamText || '분석 데이터를 Gemini에 전송 중...'}
                            </pre>
                        </div>
                    )}

                    {/* Structured Results */}
                    {analysis && (
                        <>
                            {/* Summary Cards */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                {[
                                    { label: '총 공사비', value: analysis.summary.totalAmount > 100000000 ? `₩${(analysis.summary.totalAmount/100000000).toFixed(1)}억` : `₩${analysis.summary.totalAmount?.toLocaleString()}`, color: 'text-emerald-400', icon: <BarChart3 className="w-4 h-4" /> },
                                    { label: '공종 수', value: `${analysis.summary.categoryCount}개`, color: 'text-cyan-400', icon: <Layers className="w-4 h-4" /> },
                                    { label: '항목 수', value: `${analysis.summary.itemCount}건`, color: 'text-violet-400', icon: <ClipboardList className="w-4 h-4" /> },
                                    { label: '발견 이슈', value: `${analysis.issues?.length || 0}건`, color: 'text-red-400', icon: <AlertTriangle className="w-4 h-4" /> },
                                ].map((s, i) => (
                                    <div key={i} className={`rounded-xl border p-3 ${isDark ? 'bg-gray-900/80 border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                                        <div className="flex items-center gap-1.5 mb-1">
                                            {s.icon}
                                        </div>
                                        <div className={`text-lg font-bold ${s.color}`}>{s.value}</div>
                                        <div className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{s.label}</div>
                                    </div>
                                ))}
                            </div>

                            {/* Issues */}
                            {analysis.issues && analysis.issues.length > 0 && (
                                <div className={`rounded-2xl border overflow-hidden ${isDark ? 'border-white/10 bg-gray-900/80' : 'border-gray-200 bg-white shadow-sm'}`}>
                                    <div className={`px-4 py-2.5 border-b flex items-center gap-2 ${isDark ? 'border-white/10 bg-red-500/5' : 'border-gray-100 bg-red-50'}`}>
                                        <AlertTriangle className="w-4 h-4 text-red-400" />
                                        <span className="text-xs font-bold text-red-400">이슈 감지 ({analysis.issues.length}건)</span>
                                    </div>
                                    <div className={`divide-y ${isDark ? 'divide-white/5' : 'divide-gray-100'}`}>
                                        {analysis.issues.map((issue, i) => (
                                            <div key={i} className="px-4 py-3 flex items-start gap-3">
                                                <span className="text-sm mt-0.5">{issueIcon(issue.type)}</span>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-0.5">
                                                        <span className="text-xs font-bold">{issue.item}</span>
                                                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${severityColor(issue.severity)}`}>
                                                            {issue.severity?.toUpperCase()}
                                                        </span>
                                                    </div>
                                                    <p className={`text-[11px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{issue.detail}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Category Breakdown */}
                            {analysis.categoryBreakdown && analysis.categoryBreakdown.length > 0 && (
                                <div className={`rounded-2xl border overflow-hidden ${isDark ? 'border-white/10 bg-gray-900/80' : 'border-gray-200 bg-white shadow-sm'}`}>
                                    <div className={`px-4 py-2.5 border-b ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-100 bg-gray-50'}`}>
                                        <span className="text-xs font-bold flex items-center gap-2">
                                            <BarChart3 className="w-4 h-4 text-cyan-400" /> 공종별 분석
                                        </span>
                                    </div>
                                    <div className="p-4 space-y-2">
                                        {analysis.categoryBreakdown.map((cat, i) => (
                                            <div key={i} className="flex items-center gap-3">
                                                <span className="text-xs font-bold w-16 truncate">{cat.category}</span>
                                                <div className={`flex-1 h-4 rounded-full overflow-hidden ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                                                    <div className={`h-full rounded-full bg-gradient-to-r ${config.gradient} transition-all`} style={{ width: `${Math.min(100, cat.ratio || 0)}%` }} />
                                                </div>
                                                <span className={`text-[10px] font-mono w-12 text-right ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{(cat.ratio || 0).toFixed(1)}%</span>
                                                <span className={`text-[10px] font-mono ${config.accentText} w-20 text-right`}>₩{(cat.amount || 0).toLocaleString()}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Suggestions */}
                            {analysis.suggestions && analysis.suggestions.length > 0 && (
                                <div className={`rounded-2xl border overflow-hidden ${isDark ? 'border-white/10 bg-gray-900/80' : 'border-gray-200 bg-white shadow-sm'}`}>
                                    <div className={`px-4 py-2.5 border-b ${isDark ? 'border-white/10 bg-emerald-500/5' : 'border-gray-100 bg-emerald-50'}`}>
                                        <span className="text-xs font-bold text-emerald-400 flex items-center gap-2">
                                            <Sparkles className="w-4 h-4" /> AI 개선 제안 ({analysis.suggestions.length}건)
                                        </span>
                                    </div>
                                    <div className={`divide-y ${isDark ? 'divide-white/5' : 'divide-gray-100'}`}>
                                        {analysis.suggestions.map((sug, i) => (
                                            <div key={i} className="px-4 py-3">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-xs font-bold">{sug.title}</span>
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${severityColor(sug.impact)}`}>
                                                        {sug.impact?.toUpperCase()} IMPACT
                                                    </span>
                                                </div>
                                                <p className={`text-[11px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{sug.detail}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Toolbar */}
                            <AgentToolbar title="BOQ 견적 분석" content={streamText} agentType="boq" />

                            {/* Reset */}
                            <div className="flex justify-center">
                                <button onClick={() => { setStage(0); setParseResult(null); setAnalysis(null); setStreamText(''); setAiDone(false); }}
                                    className={`px-6 py-2.5 rounded-xl text-xs font-bold cursor-pointer transition ${isDark ? 'bg-white/5 text-gray-400 hover:bg-white/10' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                                    <RefreshCw className="w-3.5 h-3.5 inline mr-2" /> 새 파일 분석
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </AgentPageLayout>
    );
}
